import { useEffect, useMemo, useState } from 'react';
import { Theme } from '@mui/material/styles';

import { createAppTheme } from '../../theme';
import { resolveLogos } from './skin-assets';
import { fetchSkinCor } from './skin-fetch';
import { readSkinFromStorage } from './skin-storage';

export interface ShellSkinState {
  theme: Theme;
  cor: string | null;
  logoHeader: string;
  logoHeaderReduzido: string;
  faviconRaw: string | null;
}

/**
 * Orquestra a resolução da cor: storage primeiro, fetch do endpoint existente
 * como fallback condicional (só quando a cor falta), default como último
 * recurso. Logos e favicon vêm direto do storage, sem depender do fallback.
 */
export function useShellSkin(): ShellSkinState {
  const skin = readSkinFromStorage();
  const { logoHeader, logoHeaderReduzido } = resolveLogos(skin);

  const [cor, setCor] = useState<string | null>(skin.cor);
  // A cor é o único estado reativo: o tema é derivado dela, o que remove a
  // possibilidade de os dois ficarem dessincronizados. A dependência é o
  // primitivo `cor`, não o objeto do storage (novo a cada render), para que a
  // referência do tema se mantenha estável e não invalide o merge do
  // ThemeProvider aninhado do shell.
  const theme = useMemo(() => createAppTheme({ cor }), [cor]);

  useEffect(() => {
    if (skin.cor) {
      return;
    }

    let cancelled = false;
    // fetchSkinCor nunca rejeita (absorve falha internamente); nenhum catch é
    // necessário aqui, e o cancelamento evita setar estado após o unmount.
    void fetchSkinCor().then((fetched) => {
      if (!cancelled && fetched) {
        setCor(fetched);
      }
    });

    return () => {
      cancelled = true;
    };
    // Roda uma única vez na montagem: o fallback depende só do storage lido
    // acima, não de estado reativo que precise disparar um novo fetch.
  }, [skin.cor]);

  return { theme, cor, logoHeader, logoHeaderReduzido, faviconRaw: skin.favicon };
}
